import { Control, useWatch } from 'react-hook-form';
import { FormField, FormItem, FormLabel, FormControl, FormMessage } from '@/components/ui/form';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useLanguage } from '@/contexts/LanguageContext';
import type { ExamFormData } from '../utils/examValidation';

interface HistoryAssessmentProps {
  control: Control<ExamFormData>;
}

export function HistoryAssessment({ control }: HistoryAssessmentProps) {
  const { t, language } = useLanguage();
  const isRTL = language === 'ar';

  // Watch values to conditionally show text fields
  const watchedChronicDiseases = useWatch({ control, name: 'history.chronicDiseases' });
  const watchedAdditionalOtherChronic = useWatch({ control, name: 'history.additionalOtherChronic' });
  const watchedAdditionalSurgery = useWatch({ control, name: 'history.additionalSurgery' });
  const watchedAdditionalMedications = useWatch({ control, name: 'history.additionalMedications' });
  const watchedAdditionalAllergies = useWatch({ control, name: 'history.additionalAllergies' });
  const watchedOtherChronicDiseasesCheck = useWatch({ control, name: 'history.otherChronicDiseasesCheck' });

  const chronicDiseaseOptions = [
    'diabetes',
    'asthma',
    'heartProblems',
    'depressionAnxiety',
    'cancer',
    'highBloodPressure',
    'highCholesterol',
    'epilepsy',
    'anemiaBloodDiseases'
  ];

  return (
    <div className={`max-h-[60vh] overflow-y-auto pe-2 space-y-6 ${isRTL ? 'rtl' : 'ltr'}`}>
      {/* Chronic Diseases */}
      <FormField
        control={control}
        name="history.chronicDiseases"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="text-base font-medium">
              {t('dashboard.examiner.examination.history.chronicDiseases')}
            </FormLabel>
            <div className="grid grid-cols-2 gap-3 mt-3">
              {chronicDiseaseOptions.map((disease) => (
                <div key={disease} className="flex items-center gap-2">
                  <Checkbox
                    id={disease}
                    checked={field.value?.includes(disease)}
                    onCheckedChange={(checked) => {
                      const currentValue = field.value || [];
                      if (checked) {
                        field.onChange([...currentValue, disease]);
                      } else {
                        field.onChange(currentValue.filter((item) => item !== disease));
                      }
                    }}
                  />
                  <Label htmlFor={disease} className="text-sm font-normal">
                    {t(`dashboard.examiner.examination.history.options.chronicDiseases.${disease}`)}
                  </Label>
                </div>
              ))}
            </div>
            <FormMessage />
          </FormItem>
        )}
      />

      {/* Additional Questions with Checkbox + Input Format */}
      <div className="space-y-4 mt-6">
        {/* Additional Other Chronic Diseases */}
        <div className="flex items-center gap-3">
          <FormField
            control={control}
            name="history.additionalOtherChronic"
            render={({ field }) => (
              <FormItem className="flex flex-row items-center gap-3 space-y-0">
                <FormControl>
                  <Checkbox
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
                <FormLabel className="text-sm font-normal">
                  {t('dashboard.examiner.examination.history.additionalQuestions.otherChronicDiseases')}
                </FormLabel>
                <FormMessage />
              </FormItem>
            )}
          />
          {watchedAdditionalOtherChronic && (
            <FormField
              control={control}
              name="history.additionalOtherChronicDetails"
              render={({ field }) => (
                <FormItem className="flex-1">
                  <FormControl>
                    <Input {...field} placeholder={t('dashboard.examiner.examination.history.specify')} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}
        </div>

        {/* Surgical Operations */}
        <div className="flex items-center gap-3">
          <FormField
            control={control}
            name="history.additionalSurgery"
            render={({ field }) => (
              <FormItem className="flex flex-row items-center gap-3 space-y-0">
                <FormControl>
                  <Checkbox
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
                <FormLabel className="text-sm font-normal">
                  {t('dashboard.examiner.examination.history.additionalQuestions.surgicalOperations')}
                </FormLabel>
                <FormMessage />
              </FormItem>
            )}
          />
          {watchedAdditionalSurgery && (
            <FormField
              control={control}
              name="history.additionalSurgeryDetails"
              render={({ field }) => (
                <FormItem className="flex-1">
                  <FormControl>
                    <Input {...field} placeholder={t('dashboard.examiner.examination.history.specify')} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}
        </div>

        {/* Medications */}
        <div className="flex items-center gap-3">
          <FormField
            control={control}
            name="history.additionalMedications"
            render={({ field }) => (
              <FormItem className="flex flex-row items-center gap-3 space-y-0">
                <FormControl>
                  <Checkbox
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
                <FormLabel className="text-sm font-normal">
                  {t('dashboard.examiner.examination.history.additionalQuestions.takeMedications')}
                </FormLabel>
                <FormMessage />
              </FormItem>
            )}
          />
          {watchedAdditionalMedications && (
            <FormField
              control={control}
              name="history.additionalMedicationsDetails"
              render={({ field }) => (
                <FormItem className="flex-1">
                  <FormControl>
                    <Input {...field} placeholder={t('dashboard.examiner.examination.history.specify')} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}
        </div>

        {/* Allergies */}
        <div className="flex items-center gap-3">
          <FormField
            control={control}
            name="history.additionalAllergies"
            render={({ field }) => (
              <FormItem className="flex flex-row items-center gap-3 space-y-0">
                <FormControl>
                  <Checkbox
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
                <FormLabel className="text-sm font-normal">
                  {t('dashboard.examiner.examination.history.additionalQuestions.allergiesQuestion')}
                </FormLabel>
                <FormMessage />
              </FormItem>
            )}
          />
          {watchedAdditionalAllergies && (
            <FormField
              control={control}
              name="history.additionalAllergiesDetails"
              render={({ field }) => (
                <FormItem className="flex-1">
                  <FormControl>
                    <Input {...field} placeholder={t('dashboard.examiner.examination.history.specify')} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}
        </div>
      </div>

      {/* Family Chronic Diseases */}
      <FormField
        control={control}
        name="history.familyChronicDiseases"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="text-base font-medium">
              {t('dashboard.examiner.examination.history.familyChronicDiseases')}
            </FormLabel>
            <div className="grid grid-cols-2 gap-3 mt-3">
              {chronicDiseaseOptions.map((disease) => (
                <div key={disease} className="flex items-center gap-2">
                  <Checkbox
                    id={`family-${disease}`}
                    checked={field.value?.includes(disease)}
                    onCheckedChange={(checked) => {
                      const currentValue = field.value || [];
                      if (checked) {
                        field.onChange([...currentValue, disease]);
                      } else {
                        field.onChange(currentValue.filter((item) => item !== disease));
                      }
                    }}
                  />
                                     <Label htmlFor={`family-${disease}`} className="text-sm font-normal">
                     {t(`dashboard.examiner.examination.history.options.chronicDiseases.${disease}`)}
                   </Label>
                </div>
              ))}
            </div>
            <FormMessage />
          </FormItem>
        )}
      />

      {/* Other Chronic Diseases Single Checkbox */}
      <div className="flex items-center gap-3">
        <FormField
          control={control}
          name="history.otherChronicDiseasesCheck"
          render={({ field }) => (
            <FormItem className="flex flex-row items-center gap-3 space-y-0">
              <FormControl>
                <Checkbox
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
              <FormLabel className="text-sm font-normal">
                {t('dashboard.examiner.examination.history.otherChronicDiseasesQuestion')}
              </FormLabel>
              <FormMessage />
            </FormItem>
          )}
        />
        {watchedOtherChronicDiseasesCheck && (
          <FormField
            control={control}
            name="history.otherChronicDiseasesDetails"
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormControl>
                  <Input {...field} placeholder={t('dashboard.examiner.examination.history.specify')} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}
      </div>
    </div>
  );
} 